import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import {GetAnalysisPipelineStep} from 'analysis-tool/AnalysisTypes';
import {InputTableApplyState} from 'analysis-tool/templates/AnalysisTemplateUtil';
import {assert} from 'Asserts';
import {PipelineStepType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {PipelineGraphQL} from 'pipelines/PipelineTypes';
import {FONT_PROXIMA_13} from 'styles/FontConstants';
import * as Colors from 'ui-library/Colors';
import {BenchlingDuotoneIconName} from 'ui-library/icons/BenchlingDuotoneIcon';
import BenchlingIcon, {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';

import {getOutputDataFrameName} from './OutputsUtil';
import ResultSchemaName from './ResultSchemaName';

export interface OutputsListProps {
  template: PipelineGraphQL;
  outputSteps: Array<GetAnalysisPipelineStep>;
  inputTables: Array<InputTableApplyState>;
}

function getIconAndTitle(
  template: PipelineGraphQL,
  step: GetAnalysisPipelineStep,
  inputTables: Array<InputTableApplyState>
): {
  icon: BenchlingDuotoneIconName | BenchlingIconName;
  title: string;
  subTitle: JSX.Element | string;
} {
  const title = getOutputDataFrameName(template, step, inputTables);
  switch (step.definition.stepType) {
    case PipelineStepType.EXPORT_DATA_CATALOG_DATASET: {
      return {icon: 'dataset', title, subTitle: 'As dataset'};
    }
    case PipelineStepType.SAVE_TO_RESULTS: {
      return {
        icon: 'table',
        title,
        subTitle: (
          <>
            As {}
            <ResultSchemaName step={step} />
          </>
        ),
      };
    }
    default:
      assert(false, `Unexpected step type: ${step.definition.stepType}`);
  }
}

export default function OutputsList({template, outputSteps, inputTables}: OutputsListProps): JSX.Element {
  const outputStepsWithoutSendToDocument = outputSteps.filter(
    (step) => step.definition.stepType !== PipelineStepType.SEND_TO_DOCUMENT
  );
  return (
    <div>
      {outputStepsWithoutSendToDocument.map((outputStep, index) => {
        const {icon, title, subTitle} = getIconAndTitle(template, outputStep, inputTables);
        return (
          <div key={`output-container-${index}`} className={css(styles.outputContainer)}>
            <BenchlingIcon icon={icon} />
            <span>{title}</span>
            <div />
            <span className={css(styles.subTitle)}>{subTitle}</span>
          </div>
        );
      })}
    </div>
  );
}

const styles = StyleSheet.create({
  outputContainer: {
    ...FONT_PROXIMA_13,
    display: 'grid',
    gridTemplateColumns: '10px auto',
    columnGap: '6px',
    alignItems: 'center',
  },
  outputInfo: {
    display: 'flex',
    flexDirection: 'column',
    marginLeft: 6,
  },
  subTitle: {
    fontStyle: 'italic',
    color: Colors.GRAY_DARK,
  },
});
